import React, { useState, useEffect } from "react";
import "./EditTaskPopup.css";
import moment from "moment";
import DatePicker from "react-datepicker";
import axios from "axios";
import { useDispatch } from "react-redux";
import { getOverviewList, updateTicket } from "../../actions/remediationMode";
import { toastr } from "react-redux-toastr";

// const API_BASE = "https://api.iodin.io/api/ticket-system";
const API_BASE = process.env.REACT_APP_API + "/api/ticket-system";

const EditTaskPopup = ({ isOpen, onClose, taskData }) => {
  const dispatch = useDispatch();
  const [ticketDetails, setTicketDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [note, setNote] = useState(taskData?.note || "");
  const [rating, setRating] = useState(taskData?.rating || "");
  const [priority, setPriority] = useState(taskData?.priority || "");
  const [status, setStatus] = useState(taskData?.status || "");
  const [dueDate, setDueDate] = useState(
    taskData?.dueDate ? new Date(taskData.dueDate) : null
  );
  const [assignedTo, setAssignedTo] = useState(taskData?.assignedTo || "");
  const [users, setUsers] = useState([]);
  const [updateMessage, setUpdateMessage] = useState("");

  const refreshData = async () => {
    try {
      // Refresh the listing data
      await dispatch(getOverviewList(taskData.organisationId, 1, ""));
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  useEffect(() => {
    if (isOpen && taskData?.recordId) {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("session");
      fetch(`${API_BASE}/${taskData.recordId}/getticketdetailsbyrecordid`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          if (data && data.status === 200 && data.data) {
            setTicketDetails(data.data);
            setRating(taskData.rating || "");
            setPriority(taskData.priority || "");
            setStatus(taskData.status || "");
            setDueDate(taskData.dueDate ? new Date(taskData.dueDate) : null);
            setAssignedTo(taskData.assignedTo || "");
            setNote(taskData.note || "");
          } else {
            setError("Failed to fetch ticket details");
          }
        })
        .catch(() => setError("Failed to fetch ticket details"))
        .finally(() => setLoading(false));
    }
  }, [isOpen, taskData]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // const response = await axios.get(
        //   `https://api.iodin.io/api/user?id=${taskData?.organisationId}`
        // );
        const response = await axios.get(
          `${process.env.REACT_API_URL}/api/user?id=${taskData?.organisationId}`
        );
        if (response.data.error === false) {
          setUsers(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };
    fetchUsers();
  }, [taskData?.organisationId]);

  const handleUpdate = async (field, value) => {
    try {
      setLoading(true);
      const data = {
        [field]: value,
        organisationId: taskData.organisationId,
      };

      // Use the updateTicket action from remediationMode
      updateTicket(taskData.recordId, data, taskData.organisationId, dispatch);

      // Update local state
      switch (field) {
        case "rating":
          setRating(value);
          break;
        case "priority":
          setPriority(value);
          break;
        case "status":
          setStatus(value);
          break;
        case "dueDate":
          setDueDate(value);
          break;
        case "assignedTo":
          setAssignedTo(value);
          break;
        case "note":
          setNote(value);
          break;
        default:
          break;
      }

      // Close the popup after 1.5 seconds
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error("Error updating ticket:", error);
      toastr.error("Error", "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const handleNoteChange = (e) => {
    const newNote = e.target.value;
    setNote(newNote);
    handleUpdate("note", newNote);
  };

  if (!isOpen) return null;

  return (
    <div className="edit-task-overlay">
      <div className="edit-task-modal">
        <div className="edit-task-header">
          <div className="header-left">
            <h2>{taskData?.taskId}</h2>
            <span className="task-type">{taskData?.actionType}</span>
          </div>
          <div className="header-right">
            <div className="status-badge" data-status={status.toLowerCase()}>
              {status || "Open"}
            </div>
            <button className="close-btn" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {loading && <div className="loading-message">Updating...</div>}

        {updateMessage && (
          <div className="update-message" role="alert">
            {updateMessage}
          </div>
        )}

        <div className="task-content">
          {/* Main Content Area */}
          <div className="main-content">
            <div className="finding-details">
              <h3>Finding Details</h3>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Description</label>
                  <div className="detail-value">
                    {taskData?.findingDetails?.description || "-"}
                  </div>
                </div>
                <div className="detail-item">
                  <label>Detail</label>
                  <div className="detail-value">
                    {taskData?.findingDetails?.detail || "-"}
                  </div>
                </div>
                <div className="detail-item full-width">
                  <label>Specific Information</label>
                  <div className="detail-value">
                    {taskData?.findingDetails?.specificInfo || "-"}
                  </div>
                </div>
              </div>
            </div>

            <div className="task-management">
              <h3>Task Management</h3>
              <div className="management-grid">
                <div className="management-item">
                  <label>Rating</label>
                  <select
                    value={rating}
                    onChange={(e) => handleUpdate("rating", e.target.value)}
                    className="select-box"
                  >
                    <option value="">Select Rating</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
                <div className="management-item">
                  <label>Priority</label>
                  <select
                    value={priority}
                    onChange={(e) => handleUpdate("priority", e.target.value)}
                    className="select-box"
                  >
                    <option value="">Select Priority</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>
                </div>
                <div className="management-item">
                  <label>Status</label>
                  <select
                    value={status}
                    onChange={(e) => handleUpdate("status", e.target.value)}
                    className="select-box"
                  >
                    <option value="">Select Status</option>
                    <option value="Open">Open</option>
                    <option value="InProgress">In Progress</option>
                    <option value="Close">Close</option>
                  </select>
                </div>
                <div className="management-item">
                  <label>Assigned To</label>
                  <select
                    value={assignedTo}
                    onChange={(e) => handleUpdate("assignedTo", e.target.value)}
                    className="select-box"
                  >
                    <option value="">Select Assignee</option>
                    {users.map((user) => (
                      <option key={user._id} value={user._id}>
                        {user.fullName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="management-item">
                  <label>Due Date</label>
                  <DatePicker
                    selected={dueDate}
                    onChange={(date) => handleUpdate("dueDate", date)}
                    className="date-picker"
                    placeholderText="Select date"
                    dateFormat="yyyy-MM-dd"
                  />
                </div>
              </div>
            </div>

            <div className="metadata-section">
              <h3>Additional Information</h3>
              <div className="metadata-grid">
                <div className="metadata-item">
                  <label>Module</label>
                  <span>{taskData?.moduleName || "-"}</span>
                </div>
                <div className="metadata-item">
                  <label>Collection</label>
                  <span>{taskData?.collectionName || "-"}</span>
                </div>
                <div className="metadata-item">
                  <label>Container ID</label>
                  <span>{taskData?.containerId || "-"}</span>
                </div>
                <div className="metadata-item">
                  <label>Created</label>
                  <span>
                    {taskData?.createdAt
                      ? moment(taskData.createdAt).format("DD-MM-YYYY HH:mm")
                      : "-"}
                  </span>
                </div>
                <div className="metadata-item">
                  <label>Last Updated</label>
                  <span>
                    {taskData?.updatedAt
                      ? moment(taskData.updatedAt).format("DD-MM-YYYY HH:mm")
                      : "-"}
                  </span>
                </div>
                <div className="metadata-item">
                  <label>Processed</label>
                  <span>{taskData?.isProcessed ? "Yes" : "No"}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Notes Sidebar */}
          <div className="notes-sidebar">
            <h3>Notes</h3>
            <div className="notes-content">
              <textarea
                value={note}
                onChange={handleNoteChange}
                placeholder="Add notes about this task..."
                rows={4}
                className="notes-textarea"
              />
              <div className="notes-info">
                <span className="last-updated">
                  Last updated:{" "}
                  {taskData?.updatedAt
                    ? moment(taskData.updatedAt).format("DD-MM-YYYY HH:mm")
                    : "-"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTaskPopup;
